import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnInit,
	Renderer2,
	ViewChild,
	OnDestroy,
} from "@angular/core";
import { filter, map, startWith } from "rxjs/operators";
import { combineLatest, Observable } from "rxjs";
import { NavigationEnd, Router } from "@angular/router";
import * as objectPath from "object-path";
// Layout
import {
	LayoutConfigService,
	MenuAsideService,
	MenuOptions,
	OffcanvasOptions,
} from "../../../core/_base/layout";
import { HtmlClassService } from "../html-class.service";
import { SharedService } from "../../../../app/shared/sharedServices/shared.service";
import { AppFormDataService } from "../../../../app/shared/sharedServices/app-form-data.service";
import { decrypt } from "../../../../app/utilities/utilityFunctions";
import { ForeignEmbassyValidatorService } from "../../pages/dashboard/services/foreign-embassy-validator.service";
import { Subscribable, Subscription } from "rxjs";
import { HEC_CONSTANTS } from "../../../utilities/constants";
import { flatMap } from "lodash";
import { LgspService } from "../../pages/lgsp/services/lgsp.service";
import { PbsDataService } from "../../pages/pbs/services/pbs-data.service";
import { SespDataService } from "../../pages/sesp/services/sesp-data.service";
import { PmwsDataService } from "../../pages/pmws/services/pmws-data.service";
import { IpfpDataService } from "../../pages/ipfp/services/ipfp-data.service";

@Component({
	selector: "kt-aside-left",
	templateUrl: "./aside-left.component.html",
	styleUrls: ["./aside-left.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AsideLeftComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild("asideMenu", { static: true }) asideMenu: ElementRef;

	currentRouteUrl = "";
	role: string;
	public view: Subscription;
	public isAuthorizePersonAvailable: boolean = false;
	private isForeignEmbassy: boolean = false;
	// derived stream that always emits menu items with the correct `allwoed` flag
	public menuItems$: Observable<any[]>;
	menuCanvasOptions: OffcanvasOptions = {
		baseClass: "kt-aside",
		overlay: true,
		closeBy: "kt_aside_close_btn",
		toggleBy: {
			target: "kt_aside_mobile_toggler",
			state: "kt-header-mobile__toolbar-toggler--active",
		},
	};

	menuOptions: MenuOptions = {
		// vertical scroll
		scroll: null,

		// submenu setup
		submenu: {
			desktop: {
				// by default the menu mode set to accordion in desktop mode
				default: "dropdown",
				// whenever body has this class switch the menu mode to dropdown
				state: {
					body: "kt-aside--minimize",
					mode: "dropdown",
				},
			},
			tablet: "accordion", // menu set to accordion in tablet mode
			mobile: "accordion", // menu set to accordion in mobile mode
		},

		// accordion setup
		accordion: {
			expandAll: false, // allow having multiple expanded accordions in the menu
		},
	};

	/**
	 * Component Conctructor
	 *
	 * @param htmlClassService: HtmlClassService
	 * @param menuAsideService
	 * @param layoutConfigService: LayouConfigService
	 * @param router: Router
	 * @param render: Renderer2
	 * @param cdr: ChangeDetectorRef
	 */
	constructor(
		public htmlClassService: HtmlClassService,
		public menuAsideService: MenuAsideService,
		public layoutConfigService: LayoutConfigService,
		private router: Router,
		private render: Renderer2,
		private cdr: ChangeDetectorRef,
		private sharedService: SharedService,
		private foreignValidator: ForeignEmbassyValidatorService,
		public lgspService: LgspService,
		private pbsDataService: PbsDataService,
		private sespDataService: SespDataService,
		private pmwsDataService: PmwsDataService,
		private ipfpDataService: IpfpDataService,
		private appFormDataService: AppFormDataService
	) {
		const profileDetails = JSON.parse(
			decrypt(localStorage.getItem("esp-profile-details"))
		);
		if (profileDetails) {
			setInterval(() => {
				this.getAuthoirizedPersonDetail();
			}, 120000);
		}
		this.role = this.sharedService.checkAdminRole(profileDetails.getSpRole)
			? "ADMIN"
			: "USER";
		// this.role = 'USER'
		// this.view = this.foreignValidator.isForeignEmbassy.asObservable()
		
		if (this.role) {
			// Derive the rendered menu from the menu list + foreign-embassy flag.
			// Binding the template to this stream guarantees every emitted item
			// already carries the correct `allwoed` value, so there is no race
			// between the async pipe and a separate mutating subscription.
			this.menuItems$ = combineLatest([
				this.menuAsideService.menuList$,
				this.foreignValidator.getForeign().pipe(startWith(null)),
			]).pipe(
				map(([items, foreign]) => {
					this.isForeignEmbassy = !!(foreign && foreign.isForeign);
					return this.applyMenuPermissions(items);
				})
			);
		}
	}

	private readonly allowedPageCodes: string[] = [
		"DH",
		"I5000",
		"merits",
		"CRHESPB",
		"LGSP",
		"Bal",
		"FDP",
		"OFSP",
		"SESP",
		"WCP",
	];

	/**
	 * Return a new list of menu items with the `allwoed` flag computed from the
	 * current role / foreign-embassy context.
	 */
	private applyMenuPermissions(items: any[]): any[] {
		if (!items || !items.length) {
			return [];
		}
		// only restrict to foreign-embassy items when such items actually exist,
		// otherwise a focal-person user would end up with a completely blank sidebar
		const hasForeignItems = items.some((el) => el.pageCode == "FE");
		const restrictToForeign = this.isForeignEmbassy && hasForeignItems;
		return items.map((element) => {
			const allwoed = restrictToForeign
				? element.pageCode == "FE"
				: this.allowedPageCodes.indexOf(element.pageCode) !== -1;
			return { ...element, allwoed };
		});
	}

	ngAfterViewInit(): void { }

	ngOnDestroy(): void {
		this.role = undefined;
		this.foreignValidator.sendForeign(false);
	}

	ngOnInit() {
		document.body.classList.remove('kt-aside--minimize');
		document.body.classList.remove('kt-aside--minimize-hover');

		this.currentRouteUrl = this.router.url.split(/[?#]/)[0];

		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe((event) => {
				this.currentRouteUrl = this.router.url.split(/[?#]/)[0];
				this.cdr.markForCheck();
			});

		const config = this.layoutConfigService.getConfig();

		if (
			objectPath.get(config, "aside.menu.dropdown") !== true &&
			objectPath.get(config, "aside.self.fixed")
		) {
			this.render.setAttribute(
				this.asideMenu.nativeElement,
				"data-ktmenu-scroll",
				"1"
			);
		}

		if (objectPath.get(config, "aside.menu.dropdown")) {
			this.render.setAttribute(
				this.asideMenu.nativeElement,
				"data-ktmenu-dropdown",
				"1"
			);
			// tslint:disable-next-line:max-line-length
			this.render.setAttribute(
				this.asideMenu.nativeElement,
				"data-ktmenu-dropdown-timeout",
				objectPath.get(
					config,
					"aside.menu.submenu.dropdown.hover-timeout"
				)
			);
		}
	}

	/**
	 * Check Menu is active
	 * @param item: any
	 */
	isMenuItemIsActive(item): boolean {
		if (item.submenu) {
			return this.isMenuRootItemIsActive(item);
		}

		if (!item.page) {
			return false;
		}

		return this.currentRouteUrl.indexOf(item.page) !== -1;
	}

	/**
	 * Check Menu Root Item is active
	 * @param item: any
	 */
	isMenuRootItemIsActive(item): boolean {
		let result = false;

		for (const subItem of item.submenu) {
			result = this.isMenuItemIsActive(subItem);
			if (result) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Returns Submenu CSS Class Name
	 * @param item: any
	 */
	getItemCssClasses(item) {
		let classes = "kt-menu__item";

		if (objectPath.get(item, "submenu")) {
			classes += " kt-menu__item--submenu";
		}

		if (!item.submenu && this.isMenuItemIsActive(item)) {
			classes += " kt-menu__item--active kt-menu__item--here";
		}

		if (item.submenu && this.isMenuItemIsActive(item)) {
			classes += " kt-menu__item--open kt-menu__item--here";
		}

		// custom class for menu item
		const customClass = objectPath.get(item, "custom-class");
		if (customClass) {
			classes += " " + customClass;
		}

		if (objectPath.get(item, "icon-only")) {
			classes += " kt-menu__item--icon-only";
		}

		return classes;
	}

	getItemAttrSubmenuToggle(item) {
		let toggle = "hover";
		if (objectPath.get(item, "toggle") === "click") {
			toggle = "click";
		} else if (objectPath.get(item, "submenu.type") === "tabs") {
			toggle = "tabs";
		} else {
			// submenu toggle default to 'hover'
		}

		return toggle;
	}

	public async getAuthoirizedPersonDetail() {
		// await this.authorizePersonService.getAuthorizePersonDetail().subscribe(
		//   (res) => {
		// 	if (res.statusCode == HEC_CONSTANTS.HTTP_RESPONSE.OK) {
		// 	  if (!res.data.applicationId) {
		// 		  this.isAuthorizePersonAvailable = false;
		// 	}else if(res.data.applicationId) {
		// 		this.isAuthorizePersonAvailable = true;
		// 		if(this.isAuthorizePersonAvailable){
		// 			this.menuAsideService.menuList$.asObservable().subscribe(
		// 				(res)=>{
		// 					res.forEach(element => {
		// 						if(element.pageCode == 'UA'){
		// 							element.allwoed = true;
		// 							this.cdr.detectChanges()
		// 						}
		// 					});
		// 				}
		// 			)
		// 		}
		// 	}
		// 	} else if (res.statusCode == 4) {
		// 		this.isAuthorizePersonAvailable = false;
		// 	}
		// 	else {
		// 		this.isAuthorizePersonAvailable = false;
		// 	}
		//   },
		//   (err) => {
		// 	this.isAuthorizePersonAvailable = false;
		//   }
		// )
	}

	onMenuClick(item: any): void {
		console.log("Menu item clicked:", item);
		if (item.pageCode == "LGSP") {
			this.lgspService.setFormViewFromSideMenu(true);
		}
		else if (item.page.includes('pbs')) {
			this.pbsDataService.setFormViewFromSideMenu(true);
		}
		else if (item.page.includes('cafc')) {
			this.pbsDataService.setFormViewFromSideMenu(true);
		}
		else if (item.page.includes('sesp')) {
			this.sespDataService.setFormViewFromSideMenu(true);
		}
		else if (item.page.includes('pmws')) {
			this.pmwsDataService.setFormViewFromSideMenu(true);
		}
		else if (item.page.includes('ipfp')) {
			localStorage.removeItem('ipfpAppId');
			localStorage.removeItem('ipfpData');
			this.ipfpDataService.setFormViewFromSideMenu(true);
			this.ipfpDataService.notifyIpfpOpenedFromSideMenu();
			this.ipfpDataService.setApplicationId(null, 'IPFP', null, false, null, null, null);
			this.clearIpfpDashboardContext();
		}
		
	}

	private clearIpfpDashboardContext(): void {
		this.appFormDataService.applicationId = undefined;
		this.appFormDataService.acronym = undefined;
		this.appFormDataService.taskName = undefined;
		this.appFormDataService.formView = undefined;
		this.appFormDataService.statusCodeId = undefined;
		this.appFormDataService.application = {};
		this.appFormDataService.isAdmin = undefined;
	}
	  
}
